import {
  FIND_CLS_ITEMS,
  GET_ACCESS_LEVEL,
  GET_AUTHORSHIP_TYPES,
  GET_CLS_ITEMS,
  GET_CONTENT_TYPES,
  GET_IDENTITY_SYSTEM,
  GET_LANGUAGES,
  GET_PUBLICATION_THEMATIC,
  GET_PUBLICATION_TYPE,
  GET_SCIENCE_AREA,
  GET_SCIENCE_FIELD,
  GET_SETTINGS_ACCESS_LEVELS,
  GET_UPLOAD_FILE_EXTENTIONS,
  GET_UPLOAD_FILE_MIME,
  GET_UPLOAD_ITEM_TYPES,
} from 'redux/constants';
import {
  FindClsItemsPayload,
  GetClsItemsPayload,
  getSettingsAccessLevelsFailed,
  getSettingsAccessLevelsSuccess,
} from 'redux/actions/clsActions';
import { call, put, takeEvery } from 'redux-saga/effects';
import {
  getAccessLevelFailed,
  getAccessLevelSuccess,
  getAuthorshipTypesFailed,
  getAuthorshipTypesSuccess,
  getContentTypesFailed,
  getContentTypesSuccess,
  getIdentitySystemFailed,
  getIdentitySystemSuccess,
  getLanguagesFailed,
  getLanguagesSuccess,
  getPublicationThematicFailed,
  getPublicationThematicSuccess,
  getPublicationTypeFailed,
  getPublicationTypeSuccess,
  getScienceAreaFailed,
  getScienceAreaSuccess,
  getScienceFieldFailed,
  getScienceFieldSuccess,
  getUploadFileExtentionsFailed,
  getUploadFileExtentionsSuccess,
  getUploadFileMimeFailed,
  getUploadFileMimeSuccess,
  getUploadItemTypesFailed,
  getUploadItemTypesSuccess,
} from 'redux/actions/clsActions';
import { ActionType } from 'types/reduxTypes';
import { ClsItem } from 'types/clsType';
import { cls } from 'constants/cls';

import config from '../../shared/config';
import safeAxios from '../../shared/api/safeFetch';

type GetClsItemsAsyncArgs = {
  cls: number;
  lang?: number;
  page?: number;
  pageSize?: number;
};

export function* getClsItemsHandler({
  payload: { cls, lang, page, pageSize, success, failed },
}: ActionType<GetClsItemsPayload>): Generator<unknown, void, { data: ClsItem[] }> {
  try {
    const { data } = yield call(getClsItemsAsync, {
      cls,
      lang,
      page,
      pageSize,
    });
    yield put(success(data));
  } catch (error) {
    yield put(failed({ message: '' }));
  }
}

export function* getUploadFileExtentionsHandler({
  payload: { lang },
}: ActionType<GetClsItemsPayload>): Generator<unknown, void, { data: ClsItem[] }> {
  yield call(getClsItemsHandler, {
    payload: {
      cls: cls.uploadFileExt,
      failed: getUploadFileExtentionsFailed,
      lang,
      pageSize: 10000,
      success: getUploadFileExtentionsSuccess,
    },
    type: GET_CLS_ITEMS,
  });
}

export function* getUploadFileMimeHandler({
  payload: { lang },
}: ActionType<GetClsItemsPayload>): Generator<unknown, void, { data: ClsItem[] }> {
  yield call(getClsItemsHandler, {
    payload: {
      cls: cls.uploadFileMime,
      failed: getUploadFileMimeFailed,
      lang,
      pageSize: 10000,
      success: getUploadFileMimeSuccess,
    },
    type: GET_CLS_ITEMS,
  });
}

export function* getPublicationTypeHandler({
  payload: { lang },
}: ActionType<GetClsItemsPayload>): Generator<unknown, void, { data: ClsItem[] }> {
  yield call(getClsItemsHandler, {
    payload: {
      cls: cls.publicationType,
      failed: getPublicationTypeFailed,
      lang,
      pageSize: 10000,
      success: getPublicationTypeSuccess,
    },
    type: GET_CLS_ITEMS,
  });
}

export function* getPublicationThematicHandler({
  payload: { lang },
}: ActionType<GetClsItemsPayload>): Generator<unknown, void, { data: ClsItem[] }> {
  yield call(findClsItemsHandler, {
    payload: {
      cls: cls.publicationThematic,
      failed: getPublicationThematicFailed,
      lang,
      level: 3,
      pageSize: 10000,
      success: getPublicationThematicSuccess,
    },
    type: FIND_CLS_ITEMS,
  });
}

export function* getScienceFieldHandler({
  payload: { lang },
}: ActionType<GetClsItemsPayload>): Generator<unknown, void, { data: ClsItem[] }> {
  yield call(getClsItemsHandler, {
    payload: {
      cls: cls.scienceField,
      failed: getScienceFieldFailed,
      lang,
      pageSize: 10000,
      success: getScienceFieldSuccess,
    },
    type: GET_CLS_ITEMS,
  });
}

export function* getScienceAreaHandler({
  payload: { lang },
}: ActionType<GetClsItemsPayload>): Generator<unknown, void, { data: ClsItem[] }> {
  yield call(getClsItemsHandler, {
    payload: {
      cls: cls.scienceArea,
      failed: getScienceAreaFailed,
      lang,
      pageSize: 10000,
      success: getScienceAreaSuccess,
    },
    type: GET_CLS_ITEMS,
  });
}

export function* getLanguagesHandler({
  payload: { lang },
}: ActionType<GetClsItemsPayload>): Generator<unknown, void, { data: ClsItem[] }> {
  yield call(getClsItemsHandler, {
    payload: {
      cls: cls.lang,
      failed: getLanguagesFailed,
      lang,
      pageSize: 10000,
      success: getLanguagesSuccess,
    },
    type: GET_CLS_ITEMS,
  });
}

export function* getAuthorshipTypesHandler({
  payload: { lang },
}: ActionType<GetClsItemsPayload>): Generator<unknown, void, { data: ClsItem[] }> {
  yield call(getClsItemsHandler, {
    payload: {
      cls: cls.authorshipType,
      failed: getAuthorshipTypesFailed,
      lang,
      pageSize: 10000,
      success: getAuthorshipTypesSuccess,
    },
    type: GET_CLS_ITEMS,
  });
}

export function* getIdentitySystemHandler({
  payload: { lang },
}: ActionType<GetClsItemsPayload>): Generator<unknown, void, { data: ClsItem[] }> {
  yield call(getClsItemsHandler, {
    payload: {
      cls: cls.identitySystem,
      failed: getIdentitySystemFailed,
      lang,
      pageSize: 10000,
      success: getIdentitySystemSuccess,
    },
    type: GET_CLS_ITEMS,
  });
}

export function* getAccessLevelHandler({
  payload: { lang },
}: ActionType<GetClsItemsPayload>): Generator<unknown, void, { data: ClsItem[] }> {
  yield call(getClsItemsHandler, {
    payload: {
      cls: cls.accessLevel,
      failed: getAccessLevelFailed,
      lang,
      pageSize: 10000,
      success: getAccessLevelSuccess,
    },
    type: GET_CLS_ITEMS,
  });
}

export function* getUploadItemTypesHandler({
  payload: { lang },
}: ActionType<GetClsItemsPayload>): Generator<unknown, void, { data: ClsItem[] }> {
  yield call(getClsItemsHandler, {
    payload: {
      cls: cls.uploadItemType,
      failed: getUploadItemTypesFailed,
      lang,
      pageSize: 10000,
      success: getUploadItemTypesSuccess,
    },
    type: GET_CLS_ITEMS,
  });
}

export function* getContentTypesHandler({
  payload: { lang },
}: ActionType<GetClsItemsPayload>): Generator<unknown, void, { data: ClsItem[] }> {
  yield call(getClsItemsHandler, {
    payload: {
      cls: cls.contentType,
      failed: getContentTypesFailed,
      lang,
      pageSize: 10000,
      success: getContentTypesSuccess,
    },
    type: GET_CLS_ITEMS,
  });
}

export function* getSettingsAccessLevelsHandler({
  payload: { lang },
}: ActionType<GetClsItemsPayload>): Generator<unknown, void, { data: ClsItem[] }> {
  yield call(getClsItemsHandler, {
    payload: {
      cls: cls.settingsAccessLevels,
      failed: getSettingsAccessLevelsFailed,
      lang,
      pageSize: 10000,
      success: getSettingsAccessLevelsSuccess,
    },
    type: GET_CLS_ITEMS,
  });
}

export const getClsItemsAsync = async ({
  cls,
  lang,
  page,
  pageSize,
}: GetClsItemsAsyncArgs): Promise<{ data: ClsItem[] }> => {
  const requestParams: string[] = [];
  requestParams.push(`cls=${cls}`);
  if (lang) {
    requestParams.push(`lang=${lang}`);
  }
  if (page) {
    requestParams.push(`page=${page}`);
  }
  if (pageSize) {
    requestParams.push(`pageSize=${pageSize}`);
  }
  const result = await safeAxios(`${config.apiUrl}/cls/all?${requestParams.join('&')}`, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'GET',
  });
  return { data: (result?.data as ClsItem[]) ?? [] };
};

export function* findClsItemsHandler({
  payload: { cls, term, lang, page, pageSize, level, success, failed },
}: ActionType<FindClsItemsPayload>): Generator<unknown, void, { data: ClsItem[] }> {
  const body = {
    cls,
    lang,
    level,
    page,
    pageSize,
    term,
  };
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/cls/find`, {
      body: body,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
    yield put(success(data));
  } catch (error) {
    yield put(failed({ message: '' }));
  }
}

export default function* fetchUploadWatcher(): Generator {
  yield takeEvery(GET_CLS_ITEMS, getClsItemsHandler);
  yield takeEvery(GET_UPLOAD_FILE_EXTENTIONS, getUploadFileExtentionsHandler);
  yield takeEvery(GET_UPLOAD_FILE_MIME, getUploadFileMimeHandler);
  yield takeEvery(GET_PUBLICATION_TYPE, getPublicationTypeHandler);
  yield takeEvery(GET_PUBLICATION_THEMATIC, getPublicationThematicHandler);
  yield takeEvery(GET_SCIENCE_FIELD, getScienceFieldHandler);
  yield takeEvery(GET_SCIENCE_AREA, getScienceAreaHandler);
  yield takeEvery(GET_LANGUAGES, getLanguagesHandler);
  yield takeEvery(GET_AUTHORSHIP_TYPES, getAuthorshipTypesHandler);
  yield takeEvery(GET_IDENTITY_SYSTEM, getIdentitySystemHandler);
  yield takeEvery(GET_ACCESS_LEVEL, getAccessLevelHandler);
  yield takeEvery(GET_UPLOAD_ITEM_TYPES, getUploadItemTypesHandler);
  yield takeEvery(GET_CONTENT_TYPES, getContentTypesHandler);
  yield takeEvery(GET_SETTINGS_ACCESS_LEVELS, getSettingsAccessLevelsHandler);
}
